import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../store/authStore';
import { api } from '../api/instance';
import { clubsBookingApi } from '../api/clubs-booking';
import type { SignInDto, SignUpDto } from '../api/api';
import { clearApplicationAuth, redirectToMarketingSite } from '@/utils/sessionLogout';

interface AuthContextType {
  isAuthenticated: boolean;
  userName: string;
  userId: string;
  authLoading: boolean;
  authError: string;
  loginData: SignInDto;
  signupData: SignUpDto;
  showLoginModal: boolean;
  showSignupModal: boolean;
  setLoginData: (data: SignInDto) => void;
  setSignupData: (data: SignUpDto) => void;
  setAuthError: (error: string) => void;
  setShowLoginModal: (show: boolean) => void;
  setShowSignupModal: (show: boolean) => void;
  handleLogin: (e: React.FormEvent) => Promise<void>;
  handleSignup: (e: React.FormEvent) => Promise<void>;
  handleLogout: () => void;
  openSignupModal: () => void;
  openLoginModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Navigation
  const navigate = useNavigate();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string>('');
  
  // Auth form data
  const [loginData, setLoginData] = useState<SignInDto>({
    email: '',
    password: ''
  });
  const [signupData, setSignupData] = useState<SignUpDto>({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    howDidYouHear: ''
  });
  
  // Modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  
  // Check authentication on component mount
  useEffect(() => {
    const user = auth.getUser();
    if (user.isAuthenticated) {
      setIsAuthenticated(true);
      setUserName(user.name || '');
      setUserId(user.userId || '');
      api.setAuthToken(user.token || '');
      clubsBookingApi.setAuthToken(user.token || '');
    }
  }, []);
  
  // Handle login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await api.login(loginData);
      const email =
        response.user?.email?.trim() || loginData.email.trim() || undefined;
      auth.setAuth(response.accessToken, response.id, response.name, email, response.role);
      setIsAuthenticated(true);
      setUserName(response.name);
      setUserId(response.id);
      setShowLoginModal(false);
      setLoginData({ email: '', password: '' });
      
      // Set the auth token on the API instance
      api.setAuthToken(response.accessToken);
      clubsBookingApi.setAuthToken(response.accessToken);
      
      // Redirect to dashboard after successful login
      navigate('/dashboard');
    } catch (error: any) {
      setAuthError(error.response?.data?.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Handle signup
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await api.signup(signupData);
      const email =
        response.user?.email?.trim() || signupData.email.trim() || undefined;
      auth.setAuth(response.accessToken, response.id, response.name, email, response.role);
      setIsAuthenticated(true);
      setUserName(response.name);
      setUserId(response.id);
      setShowSignupModal(false);
      setSignupData({ email: '', name: '', password: '', confirmPassword: '', howDidYouHear: '' });
      
      // Set the auth token on the API instance
      api.setAuthToken(response.accessToken);
      clubsBookingApi.setAuthToken(response.accessToken);
      
      // Redirect to dashboard after successful signup
      navigate('/dashboard');
    } catch (error: any) {
      setAuthError(error.response?.data?.message || 'Signup failed');
    } finally {
      setAuthLoading(false);
    }
  };
  
  
  // Handle logout
  const handleLogout = () => {
    api.clearAuthToken();
    clubsBookingApi.clearAuthToken();
    clearApplicationAuth();
    setIsAuthenticated(false);
    setUserName('');
    setUserId('');
    navigate('/', { replace: true });
    redirectToMarketingSite();
  };

  // Helper functions to open modals
  const openSignupModal = () => {
    setShowLoginModal(false);
    setShowSignupModal(true);
  };

  const openLoginModal = () => {
    setShowSignupModal(false);
    setShowLoginModal(true);
  };

  const value: AuthContextType = {
    isAuthenticated,
    userName,
    userId,
    authLoading,
    authError,
    loginData,
    signupData,
    showLoginModal,
    showSignupModal,
    setLoginData,
    setSignupData,
    setAuthError,
    setShowLoginModal,
    setShowSignupModal,
    handleLogin,
    handleSignup,
    handleLogout,
    openSignupModal,
    openLoginModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
